/**
 * Páginas públicas de retorno do checkout Asaas: /obrigado, /cancelado, /expirado.
 *
 * O Asaas manda o comprador para cá depois do checkout (callback do link de pagamento).
 *   ?p=dr-salus&s=<subscriber_id>  → assinatura/cobrança única do Dr. Salus
 *                                    (faz polling do status até `active` e mostra o botão do WhatsApp)
 *   ?p=desafio&order_nsu=…&transaction_nsu=…&slug=… → Desafio pela InfinitePay (confirma o pagamento
 *                                    pelo backend e faz polling até a inscrição no follow-up)
 *   (sem parâmetros)                → produto do Desafio (link do Stripe/Asaas)
 *
 * Sem autenticação. Não usa o axios com interceptor de 401 (senão redirecionaria pro /login).
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://quora-backend-cl3.kinddesert-624c9557.brazilsouth.azurecontainerapps.io';
const SITE_KEY = process.env.REACT_APP_PUBLIC_SITE_KEY || '';

const COPY = {
  obrigado: {
    'dr-salus': {
      title: 'Pagamento recebido! 🎉',
      text: 'Sua assinatura do Dr. Salus está sendo ativada. Assim que confirmar, é só abrir o WhatsApp e mandar um "oi" — o Dr. Salus começa o seu cadastro na hora.',
      waiting: 'Confirmando o pagamento com o banco… isso costuma levar alguns segundos (boleto pode levar até 1 dia útil).',
      button: 'Abrir o WhatsApp do Dr. Salus',
    },
    desafio: {
      title: 'Bem-vindo(a) ao Desafio! 🎉',
      text: 'Pagamento recebido. A primeira mensagem do Desafio Meu Intestino Blindado chega no seu WhatsApp em alguns minutos, pelo número do Desafio (o mesmo número que você informou no pagamento). Se preferir, mande um "oi" por lá agora.',
      button: 'Abrir o WhatsApp do Desafio',
    },
  },
  cancelado: {
    title: 'Pagamento não concluído',
    text: 'Nenhuma cobrança foi feita. Se quiser tentar de novo, é só voltar ao link de pagamento. Qualquer dúvida, fale com a gente no WhatsApp.',
  },
  expirado: {
    title: 'Este link expirou',
    text: 'O link de pagamento perdeu a validade. Peça um novo link pelo WhatsApp ou pela página do produto.',
  },
};

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

async function apiGet(path) {
  const headers = SITE_KEY ? { 'x-site-key': SITE_KEY } : {};
  const r = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

export default function CheckoutResultPage({ kind = 'obrigado' }) {
  const q = useQuery();
  const product = q.get('p') === 'dr-salus' ? 'dr-salus' : 'desafio';
  const subscriberId = q.get('s');
  const orderNsu = q.get('order_nsu');
  const txNsu = q.get('transaction_nsu');
  const slug = q.get('slug');
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [tries, setTries] = useState(0);
  const [desafio, setDesafio] = useState(null);

  // Desafio (InfinitePay): confirma pelo redirect e espera a inscrição (máx ~2 min)
  useEffect(() => {
    if (kind !== 'obrigado' || product !== 'desafio' || !orderNsu) return undefined;
    let cancelled = false; let timer = null;
    const tick = async () => {
      try {
        const qs = txNsu && slug ? `?transaction_nsu=${encodeURIComponent(txNsu)}&slug=${encodeURIComponent(slug)}` : '';
        const s = await apiGet(`/api/public/desafios/checkout/${encodeURIComponent(orderNsu)}${qs}`);
        if (cancelled) return;
        setDesafio(s);
        if (!(s.paid && s.enrolled) && tries < 24) timer = setTimeout(() => setTries((t) => t + 1), 5000);
      } catch (e) {
        if (!cancelled && tries < 24) timer = setTimeout(() => setTries((t) => t + 1), 8000);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [kind, product, orderNsu, txNsu, slug, tries]);

  useEffect(() => {
    apiGet('/api/public/config').then(setConfig).catch(() => setConfig(null));
  }, []);

  // Dr. Salus: polling do status até ativar (máx ~5 min)
  useEffect(() => {
    if (kind !== 'obrigado' || product !== 'dr-salus' || !subscriberId) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        // InfinitePay (03/11/2026): repassa transaction_nsu/slug do redirect para confirmar sem esperar o webhook.
        const qs = txNsu && slug ? `?transaction_nsu=${encodeURIComponent(txNsu)}&slug=${encodeURIComponent(slug)}` : '';
        const s = await apiGet(`/api/public/dr-salus/checkout/${encodeURIComponent(subscriberId)}${qs}`);
        if (cancelled) return;
        setStatus(s);
        if (!s.active && tries < 60) {
          timer = setTimeout(() => setTries((t) => t + 1), 5000);
        }
      } catch (e) {
        if (!cancelled && tries < 60) timer = setTimeout(() => setTries((t) => t + 1), 8000);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [kind, product, subscriberId, tries]);

  const waUrl = product === 'dr-salus'
    ? (status?.whatsapp_url || config?.dr_salus?.whatsapp_url)
    : (desafio?.whatsapp_url || config?.desafios?.whatsapp_url);
  const waNumber = product === 'dr-salus' ? config?.dr_salus?.whatsapp_number : config?.desafios?.whatsapp_number;

  const copy = kind === 'obrigado' ? COPY.obrigado[product] : COPY[kind];
  const desafioPendente = product === 'desafio' && !!orderNsu && !(desafio?.paid && desafio?.enrolled);
  const active = (product !== 'dr-salus' || !subscriberId || status?.active) && !desafioPendente;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brand}>{product === 'dr-salus' ? 'Dr. Salus' : 'Desafio Meu Intestino Blindado'}</div>
        <h1 style={styles.title}>{copy.title}</h1>
        <p style={styles.text}>{copy.text}</p>

        {kind === 'obrigado' && product === 'dr-salus' && subscriberId && !active && (
          <div style={styles.waiting}>
            <span style={styles.spinner} /> {COPY.obrigado['dr-salus'].waiting}
          </div>
        )}

        {kind === 'obrigado' && product === 'desafio' && orderNsu && (
          desafioPendente ? (
            <div style={{ ...styles.waiting, display: 'flex' }}>
              <span style={styles.spinner} /> {desafio?.paid
                ? 'Pagamento confirmado! Inscrevendo o seu WhatsApp no Desafio…'
                : 'Confirmando o pagamento… Pix costuma levar alguns segundos. Pode deixar esta página aberta.'}
            </div>
          ) : (
            <div style={{ ...styles.waiting, background: '#ecfdf5', color: '#065f46', display: 'block' }}>
              ✅ Inscrição confirmada{desafio?.product_name ? ` no ${desafio.product_name}` : ''}. A mensagem de abertura já está a caminho do seu WhatsApp: responda <strong>SIM</strong> para receber o material do Dia 1.
            </div>
          )
        )}

        {kind === 'obrigado' && (
          <a
            href={waUrl || '#'}
            style={{ ...styles.button, opacity: waUrl && active ? 1 : 0.5, pointerEvents: waUrl && active ? 'auto' : 'none' }}
            target="_blank"
            rel="noreferrer"
          >
            {copy.button}
          </a>
        )}

        {kind !== 'obrigado' && waUrl && (
          <a href={waUrl} style={styles.buttonSecondary} target="_blank" rel="noreferrer">Falar no WhatsApp</a>
        )}

        {waNumber && (
          <p style={styles.small}>
            Número oficial: +{waNumber}. {product === 'dr-salus'
              ? 'Use o mesmo número de WhatsApp informado no pagamento — é ele que libera o acesso.'
              : 'As mensagens do Desafio chegam por este número.'}
          </p>
        )}
      </div>
      <style>{`@keyframes crp-spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, #0b3d2e 0%, #111b21 100%)', padding: 24,
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  },
  card: {
    background: '#ffffff', color: '#1f2937', borderRadius: 16, padding: '36px 32px', maxWidth: 520, width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)', textAlign: 'center',
  },
  brand: { fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: '#10b981', fontWeight: 700, marginBottom: 12 },
  title: { fontSize: 26, margin: '0 0 12px', lineHeight: 1.25 },
  text: { fontSize: 15, lineHeight: 1.6, color: '#374151', margin: '0 0 20px' },
  waiting: {
    display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#6b7280', background: '#f3f4f6',
    borderRadius: 10, padding: '10px 14px', marginBottom: 16, textAlign: 'left',
  },
  spinner: {
    width: 16, height: 16, border: '2px solid #d1d5db', borderTopColor: '#10b981', borderRadius: '50%',
    display: 'inline-block', animation: 'crp-spin 1s linear infinite', flexShrink: 0,
  },
  button: {
    display: 'inline-block', background: '#25D366', color: '#fff', textDecoration: 'none', fontWeight: 700,
    padding: '14px 22px', borderRadius: 12, fontSize: 16,
  },
  buttonSecondary: {
    display: 'inline-block', background: '#111b21', color: '#fff', textDecoration: 'none', fontWeight: 600,
    padding: '12px 20px', borderRadius: 12, fontSize: 15,
  },
  small: { fontSize: 12, color: '#6b7280', marginTop: 18, lineHeight: 1.5 },
};
